import { createId } from '@/core/id';
import { chatError, } from '@/core/types';
import { HttpError, toChatError } from '@/transport/errors';
import { createLogger } from '@/core/logger';
import { AgentEventStream } from './agentStream';
import { TextPacer } from './textPacer';
import { parseJsonLenient } from './json';
const AGENT_AUTHOR = { id: 'agent', name: 'Assistant', kind: 'ai' };
/** A person's typing dots go after this long without a ping (the console pings every ~3s). */
const HUMAN_TYPING_IDLE_MS = 7000;
/**
 * The AI drafting for a person sends one "typing" and one "stopped", not
 * pings, and a draft can take a while: the dots stay until it stops, or this.
 */
const AI_DRAFTING_MAX_MS = 180_000;
const DEFAULT_VISITOR_KEY = 'agentic-chat:visitor';
/**
 * Must exceed the backend's longest turn (TURN_TIMEOUT_SECONDS = 500 in
 * trinity). Giving up sooner does not stop the turn: it keeps running, and the
 * visitor's next message queues behind it — so that message times out too.
 */
const DEFAULT_TIMEOUT_MS = 9 * 60_000;
/** Safety stop so a broken `hasMore` can't spin forever. */
const MAX_HISTORY_PAGES = 20;
const DEFAULT_STREAM_OPEN_TIMEOUT_MS = 3_000;
/** After a stream gives up (streaming off, Redis down), wait this long before trying again. */
const STREAM_RETRY_AFTER_MS = 60_000;
/** After the POST answers, how long the paced text may take to finish showing. */
const PACER_DRAIN_MAX_MS = 3_000;
/** A chat's stream from its first event: see AgentEventStreamOptions.from. */
const FROM_START = 'public:0-0';
/**
 * When the POST fails but the turn is still running (a cut connection, a proxy
 * timeout, the API answering "still working"), how long to keep waiting for the
 * reply on the stream. An agentic turn can take minutes and retry; the visitor
 * must get the reply the console gets, not an error the console never shows.
 */
const STREAM_RESULT_WAIT_MS = 15 * 60_000;
/**
 * Adapter for the Agent Chat REST API.
 *
 * Two endpoints, and the first one is special:
 *
 *   POST /api/v1/chats                      create + answer first message
 *   POST /api/v1/chats/{chatId}/messages    every message after that
 *
 * Because creation and the first exchange are one call, this transport
 * defers creation: `createConversation()` either bootstraps (when
 * `bootstrapMessage` is set) or hands back a placeholder id, and the first
 * `sendMessage()` performs the real POST and reports the server's `chatId`
 * back to the engine via a `conversation.update` event.
 *
 * `sessionId` is returned once, on creation, and must accompany every
 * follow-up; it is held here so nothing upstream has to know it exists.
 */
export class AgentApiTransport {
    options;
    name = 'agent-api';
    /** Streaming follows `options.streaming`; the UI degrades from this. */
    capabilities;
    /** One warning per page load, not one per message. */
    static warnedAboutJson = false;
    chatId;
    sessionId;
    /**
     * Bumped by every createConversation(). A resume or first message still in
     * flight from before — the visitor pressed "new chat" mid-request — must not
     * write its chat ids back, or the next message would land in the old chat.
     */
    generation = 0;
    /** Greeting produced during bootstrap, handed to the engine via loadHistory. */
    seeded;
    /** The visitor token from /widget/session, and when to stop trusting it. */
    token;
    tokenExpiresAt = 0;
    /** One session request at a time: concurrent calls share it. */
    opening;
    /** The current session was signed in by the host app's identity token. */
    signedIn = false;
    doFetch;
    origin;
    prefix;
    logger;
    /** The live stream of the current chat, when streaming is on. */
    stream;
    /**
     * Who answers this chat, as the server last said: `human` while a person in
     * the console holds it. Then a message gets no AI reply and no typing dots.
     */
    handler = 'ai';
    /**
     * Turns nobody just asked for: the AI answering what the visitor sent while
     * a human held the chat, once it was handed back. Shown on their own, never
     * in the bubble of a message the visitor is waiting on.
     */
    unprompted = new Set();
    /** Clears a person's typing dots when their pings stop (a lost "stopped"). */
    typingTimer;
    /**
     * Who is writing a reply for the visitor, kept apart: the AI's draft waiting
     * on review keeps the dots up while the person pauses, clicks away, or
     * reads it. Either one is enough for the dots.
     */
    writing = { ai: false, human: false };
    aiTimer;
    streamChatId;
    inFlight;
    /** The engine's own emit, for messages nobody asked for (a human agent). */
    push;
    constructor(options) {
        this.options = options;
        this.doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
        this.prefix = (options.apiPrefix ?? '/api/v1').replace(/\/$/, '');
        // Constructed before the engine can inject its logger, so start with a
        // real one — otherwise the baseUrl warning below would be swallowed.
        this.logger = options.logger ?? createLogger({ prefix: 'transport' });
        this.origin = normaliseOrigin(options.baseUrl, this.prefix, this.logger);
        const streaming = options.streaming !== false;
        this.capabilities = {
            streaming,
            push: streaming,
            history: true,
            feedback: false,
            handoff: false,
        };
    }
    /** Kept for the live stream: a human agent's reply arrives unprompted. */
    connect(emit) {
        this.push = emit;
    }
    dispose() {
        this.closeStream();
    }
    setLogger(logger) {
        // Only adopt the engine's logger if one wasn't supplied explicitly.
        if (!this.options.logger)
            this.logger = logger;
    }
    async createConversation(input) {
        // A new conversation, including one after reset(), starts with no chat.
        // Keeping the old ids would post the next message into the old chat.
        this.generation++;
        this.chatId = undefined;
        this.sessionId = undefined;
        this.seeded = undefined;
        this.handler = 'ai';
        this.unprompted.clear();
        clearTimeout(this.typingTimer);
        clearTimeout(this.aiTimer);
        this.writing = { ai: false, human: false };
        this.closeStream();
        if (!this.options.bootstrapMessage) {
            // Nothing to say yet — hold a local id and make no request at all, so
            // opening the widget costs the backend nothing. `pending` tells the
            // engine not to persist this id: it isn't a real chat yet.
            this.logger.debug('deferring chat creation until the first message');
            return {
                id: createId('pending'),
                createdAt: Date.now(),
                status: 'open',
                metadata: { pending: true },
            };
        }
        const data = await this.startChat(this.options.bootstrapMessage, input.context);
        this.seeded = this.toMessage(data.messageId, data.chatId, data.reply, data);
        return {
            id: data.chatId,
            createdAt: Date.parse(data.createdAt) || Date.now(),
            status: data.status === 'active' ? 'open' : 'closed',
            metadata: { sessionId: data.sessionId, activeSkills: data.activeSkills, channel: data.channel },
        };
    }
    /**
     * Re-attach to a chat from a previous page load.
     *
     * `sessionId` is read back from the server rather than trusted from storage,
     * so a cleared or stale localStorage still resumes correctly — and a chat
     * that has been ended server-side is refused here, which makes the engine
     * start a fresh one.
     */
    async resume(chatId, options = {}) {
        try {
            const generation = this.generation;
            const status = await this.get(`${this.prefix}/chats/${encodeURIComponent(chatId)}`);
            if (generation !== this.generation) {
                this.logger.debug('resume superseded by a new conversation', { chatId });
                return null;
            }
            // `status` is the only end-of-life signal. `isComplete` means the active
            // skill has gathered its data (lead capture fills it in after the email)
            // while the visitor keeps chatting — treating it as terminal spawned a
            // new chat, and a new backend workflow, on every refresh from then on.
            // A chat of the other kind is not this widget's to resume: a data-mode
            // widget only continues a private chat, the ordinary one never does.
            if ((status.visibility === 'private') !== (this.options.mode === 'data')) {
                this.logger.info('cannot resume: chat is of the other mode', { chatId, visibility: status.visibility });
                return null;
            }
            // Picked from the history, an ended chat opens anyway: the server starts
            // a new sitting on the same thread with the next message.
            if (status.status !== 'active' && !options.reopen) {
                this.logger.info('cannot resume: chat is no longer active', {
                    chatId,
                    status: status.status,
                    isComplete: status.isComplete,
                });
                return null;
            }
            this.chatId = status.chatId;
            this.sessionId = status.sessionId;
            this.handler = status.handler === 'human' ? 'human' : 'ai';
            this.logger.debug('resumable', { chatId, messageCount: status.messageCount });
            // Listening from now on, so a reply (or a human agent) is never missed.
            if (this.capabilities.streaming)
                this.ensureStream(status.chatId);
            return {
                id: status.chatId,
                createdAt: (status.createdAt && Date.parse(status.createdAt)) || Date.now(),
                status: 'open',
                metadata: {
                    sessionId: status.sessionId,
                    records: status.records,
                    messageCount: status.messageCount,
                    channel: status.channel,
                },
            };
        }
        catch (err) {
            // Unknown chat, expired, or the endpoint is unavailable — start fresh
            // rather than stranding the visitor on a dead conversation.
            const status = err instanceof HttpError ? err.status : undefined;
            this.logger.warn('cannot resume: GET /chats/{id} failed', {
                chatId,
                httpStatus: status,
                error: err instanceof Error ? err.message : String(err),
            });
            return null;
        }
    }
    /**
     * GET /api/v1/chats/private/history — the signed-in person's own private
     * chats under the access they have now. Data mode only; [] otherwise.
     */
    async listPastChats() {
        if (this.options.mode !== 'data')
            return [];
        const data = await this.get(`${this.prefix}/chats/private/history?limit=50`);
        return (data.chats ?? []).map((chat) => ({
            id: chat.chatId,
            title: chat.title ?? '',
            status: chat.status === 'active' ? 'open' : 'closed',
            messageCount: chat.messageCount ?? 0,
            createdAt: (chat.createdAt && Date.parse(chat.createdAt)) || 0,
            lastActivityAt: (chat.lastActivityAt && Date.parse(chat.lastActivityAt)) || 0,
        }));
    }
    /** DELETE /api/v1/chats/private/{chatId} — the person deletes one of their own. */
    async deletePastChat(chatId) {
        await this.send(`${this.prefix}/chats/private/${encodeURIComponent(chatId)}`, { method: 'DELETE' });
    }
    /**
     * On a fresh start this hands back the bootstrap greeting held in memory.
     * On resume it pages the stored transcript back from the server.
     */
    async loadHistory(chatId) {
        if (this.seeded) {
            const seeded = this.seeded;
            this.seeded = undefined;
            return [seeded];
        }
        if (!this.chatId)
            return [];
        const messages = [];
        let after;
        // `limit` is capped at 100 server-side, so page until the tail is reached.
        for (let page = 0; page < MAX_HISTORY_PAGES; page++) {
            const query = new URLSearchParams({ limit: '100', order: 'asc' });
            if (after)
                query.set('after', after);
            const data = await this.get(`${this.prefix}/chats/${encodeURIComponent(chatId)}/messages?${query}`);
            const batch = data.messages ?? [];
            messages.push(...batch.map((m) => this.fromTranscript(m, chatId)));
            if (page === 0)
                this.dropBootstrapEcho(messages);
            if (!data.hasMore || !batch.length)
                break;
            after = batch[batch.length - 1]?.id;
            if (!after)
                break;
        }
        return messages;
    }
    async sendMessage({ message, replyId, context }, emit, signal) {
        try {
            // A person holds the chat: they will answer, not the AI — no dots.
            if (this.handler !== 'human')
                emit({ type: 'typing', isTyping: true });
            // STREAMING: open the chat first (no message), then listen, then send —
            // or the first tokens could be published before anyone is listening.
            let opened = false;
            if (this.capabilities.streaming && !this.chatId) {
                const chat = await this.openChat(context, signal);
                opened = true;
                emit({
                    type: 'conversation.update',
                    conversation: { id: chat.chatId, metadata: { sessionId: chat.sessionId } },
                });
            }
            if (this.capabilities.streaming && this.chatId) {
                // A chat opened just now is read from its first event, so its reply
                // streams even if the connection is slow to open.
                const stream = this.ensureStream(this.chatId, opened ? FROM_START : undefined);
                await stream.waitReady(this.options.streamOpenTimeoutMs ?? DEFAULT_STREAM_OPEN_TIMEOUT_MS);
                if (stream.alive) {
                    this.inFlight = {
                        replyId,
                        chatId: this.chatId,
                        emit,
                        started: false,
                        pacer: new TextPacer((text) => emit({ type: 'message.delta', messageId: replyId, delta: text })),
                    };
                }
            }
            // Capture this before the call: startChat() populates `chatId` as a side
            // effect, so checking afterwards would always look like "no change".
            const deferred = !this.chatId;
            const data = deferred
                ? await this.startChat(message.content, context, signal)
                : await this.postMessage(message.content, context, signal);
            // On the deferred path the engine is still holding a placeholder id, so
            // tell it the real one the server just minted.
            if (deferred) {
                emit({
                    type: 'conversation.update',
                    conversation: {
                        id: data.chatId,
                        metadata: { sessionId: this.sessionId },
                    },
                });
            }
            if (data.handledBy === 'human') {
                // Nothing was generated: a person holds the chat, and their reply
                // arrives on the live stream (showHumanMessage). No bubble here.
                this.handler = 'human';
                const flight = this.inFlight?.replyId === replyId ? this.inFlight : undefined;
                if (flight) {
                    flight.pacer.clear();
                    if (this.inFlight === flight)
                        this.inFlight = undefined;
                }
                emit({ type: 'typing', isTyping: false });
                return;
            }
            const reply = this.toMessage(data.messageId, data.chatId, data.reply, data);
            // The POST is the source of truth: a streamed bubble is finished with
            // the saved reply, which is what a reload would show — once the paced
            // text has caught up, so the reply is seen being written to the end.
            const flight = this.inFlight?.replyId === replyId ? this.inFlight : undefined;
            const streamed = Boolean(flight?.started);
            if (flight) {
                await Promise.race([flight.pacer.drain(), new Promise((r) => setTimeout(r, PACER_DRAIN_MAX_MS))]);
                flight.pacer.clear();
            }
            if (this.inFlight === flight)
                this.inFlight = undefined;
            emit({ type: 'typing', isTyping: false });
            if (!streamed) {
                emit({ type: 'message.start', message: { ...reply, id: replyId, content: '', status: 'pending' } });
                emit({ type: 'message.delta', messageId: replyId, delta: reply.content });
            }
            if (reply.blocks?.length) {
                emit({ type: 'message.blocks', messageId: replyId, blocks: reply.blocks });
            }
            if (reply.sources?.length) {
                emit({ type: 'message.sources', messageId: replyId, sources: reply.sources });
            }
            if (reply.actions?.length) {
                emit({ type: 'message.actions', messageId: replyId, actions: reply.actions });
            }
            if (reply.suggestions?.length) {
                emit({ type: 'message.suggestions', messageId: replyId, suggestions: reply.suggestions });
            }
            emit({
                type: 'message.complete',
                messageId: replyId,
                message: streamed ? { content: reply.content, metadata: reply.metadata } : { metadata: reply.metadata },
            });
        }
        catch (err) {
            const flight = this.inFlight?.replyId === replyId ? this.inFlight : undefined;
            const error = toChatError(err);
            // THE REQUEST FAILED, NOT NECESSARILY THE TURN. If the stream is still
            // following this turn, its saved reply arrives there (as it does in the
            // console): wait for it rather than show an error over a reply that lands.
            if (flight && error.code !== 'aborted' && isStillRunning(err)) {
                this.logger.info('reply request ended early; waiting for the reply on the live stream', {
                    error: error.message,
                });
                const saved = await this.awaitStreamedReply(flight, signal);
                if (saved) {
                    await this.finishFromStream(flight, saved, emit);
                    return;
                }
            }
            if (flight) {
                flight.pacer.clear();
                if (this.inFlight === flight)
                    this.inFlight = undefined;
            }
            emit({ type: 'typing', isTyping: false });
            if (error.code === 'aborted')
                return;
            emit({ type: 'message.error', messageId: replyId, error });
        }
    }
    /** The turn's saved reply from the stream, or null if it does not come in time. */
    awaitStreamedReply(flight, signal) {
        if (flight.completed)
            return Promise.resolve(flight.completed);
        return new Promise((resolve) => {
            const done = (message) => {
                clearTimeout(timer);
                signal.removeEventListener('abort', abort);
                flight.onCompleted = undefined;
                resolve(message);
            };
            const abort = () => done(null);
            const timer = setTimeout(() => done(null), STREAM_RESULT_WAIT_MS);
            signal.addEventListener('abort', abort, { once: true });
            flight.onCompleted = (message) => done(message);
        });
    }
    /** Finish the bubble from the stream's saved reply, as the POST would have. */
    async finishFromStream(flight, saved, emit) {
        await Promise.race([flight.pacer.drain(), new Promise((r) => setTimeout(r, PACER_DRAIN_MAX_MS))]);
        flight.pacer.clear();
        if (this.inFlight === flight)
            this.inFlight = undefined;
        emit({ type: 'typing', isTyping: false });
        if (!flight.started) {
            emit({ type: 'message.start', message: { ...this.streamingShell(flight), status: 'pending' } });
        }
        const suggestions = normaliseSuggestedActions(saved.suggestedActions);
        if (suggestions?.length)
            emit({ type: 'message.suggestions', messageId: flight.replyId, suggestions });
        const blocks = normaliseContent(saved.blocks ?? undefined);
        if (blocks?.length)
            emit({ type: 'message.blocks', messageId: flight.replyId, blocks });
        emit({ type: 'message.complete', messageId: flight.replyId, message: { content: saved.content } });
    }
    /** POST /api/v1/chats — creates the chat and answers in one call. */
    async startChat(message, context, signal) {
        const generation = this.generation;
        // Session first: on a first visit it is what issues the visitor id below.
        await this.authHeader();
        const data = await this.request(`${this.prefix}/chats`, {
            message,
            visitorId: this.resolveVisitorId(),
            channel: this.options.channel ?? 'web',
            ...this.modeField(),
            metadata: this.buildMetadata(context),
        }, signal);
        // Superseded while the request was out: that chat belongs to a
        // conversation the visitor has already left. Surfaces as a cancellation,
        // which sendMessage() drops silently.
        if (generation !== this.generation) {
            throw new DOMException('Superseded by a new conversation.', 'AbortError');
        }
        this.chatId = data.chatId;
        this.sessionId = data.sessionId;
        return data;
    }
    /**
     * POST /api/v1/chats with no message — the chat exists, nothing is answered.
     * Streaming needs the chat id before the first message is sent.
     */
    async openChat(context, signal) {
        const generation = this.generation;
        await this.authHeader();
        const data = await this.request(`${this.prefix}/chats`, {
            visitorId: this.resolveVisitorId(),
            channel: this.options.channel ?? 'web',
            ...this.modeField(),
            metadata: this.buildMetadata(context),
        }, signal);
        if (generation !== this.generation) {
            throw new DOMException('Superseded by a new conversation.', 'AbortError');
        }
        this.chatId = data.chatId;
        this.sessionId = data.sessionId;
        return data;
    }
    /* ---------------------------------------------------------------------- */
    /* Live stream                                                             */
    /* ---------------------------------------------------------------------- */
    /** The current chat's stream, (re)started if it is not running. */
    ensureStream(chatId, from) {
        if (this.stream && this.streamChatId === chatId) {
            // Alive, or gave up recently: a dead stream answers "not live" at once,
            // so messages are not each held up re-trying a server without streaming.
            if (this.stream.alive || Date.now() - this.stream.stoppedAt < STREAM_RETRY_AFTER_MS)
                return this.stream;
        }
        this.closeStream();
        const stream = new AgentEventStream({
            url: `${this.origin}${this.prefix}/streams/chats/${encodeURIComponent(chatId)}`,
            headers: (force) => this.authHeader(force),
            fetchImpl: this.doFetch,
            logger: this.logger,
            onEvent: (event) => this.onStreamEvent(chatId, event),
            from,
        });
        this.stream = stream;
        this.streamChatId = chatId;
        stream.start();
        return stream;
    }
    closeStream() {
        this.stream?.close();
        this.stream = undefined;
        this.streamChatId = undefined;
        this.inFlight?.pacer.clear();
        this.inFlight = undefined;
    }
    /**
     * A live event, mapped onto the engine's own. The AI's reply is written into
     * the bubble sendMessage() is waiting on; a message from a human agent is
     * shown on its own; everything else is the POST's to deliver.
     */
    onStreamEvent(chatId, event) {
        if (chatId !== this.chatId)
            return;
        const flight = this.inFlight?.chatId === chatId ? this.inFlight : undefined;
        const turnId = 'turnId' in event ? event.turnId : undefined;
        if (event.type === 'turn.started' && event.unprompted && turnId) {
            this.unprompted.add(turnId);
            if (!flight)
                this.push?.({ type: 'typing', isTyping: true });
            return;
        }
        if (turnId && this.unprompted.has(turnId)) {
            this.onUnpromptedEvent(event, turnId, Boolean(flight));
            return;
        }
        switch (event.type) {
            case 'turn.started':
                // A retried turn streams from scratch: empty the bubble it started.
                if (flight?.started && event.attempt !== flight.attempt) {
                    flight.pacer.clear();
                    flight.emit({ type: 'message.start', message: this.streamingShell(flight) });
                }
                if (flight)
                    flight.attempt = event.attempt;
                return;
            case 'turn.status':
                // The stage, and `progress`: the line the server wrote for the visitor
                // ("Checking pricing…"). The staff-facing `label` is the console's.
                if (flight && !flight.started) {
                    flight.emit(event.stage || event.progress
                        ? { type: 'status', stage: event.stage ?? '', ...(event.progress ? { text: event.progress } : {}) }
                        : { type: 'typing', isTyping: true });
                }
                return;
            case 'message.delta':
                if (event.role === 'human_agent') {
                    this.showHumanMessage(event.messageId, event.delta, true);
                    return;
                }
                if (!flight)
                    return;
                if (!flight.started) {
                    flight.started = true;
                    flight.emit({ type: 'typing', isTyping: false });
                    flight.emit({ type: 'message.start', message: this.streamingShell(flight) });
                }
                flight.pacer.push(event.delta);
                return;
            case 'message.paused':
                // Once the paced text has caught up, stop the cursor: the turn is
                // still finishing (writing the follow-up), but nothing is being typed.
                if (flight?.started) {
                    void flight.pacer.drain().then(() => {
                        if (this.inFlight === flight)
                            flight.emit({ type: 'message.pause', messageId: flight.replyId });
                    });
                }
                return;
            case 'message.created':
            case 'message.completed':
                // The saved reply, as the console sees it. Kept for the case where the
                // POST that asked for it does not come back (see sendMessage).
                if (event.type === 'message.completed' && event.message.role === 'ai_agent' && flight) {
                    flight.completed = event.message;
                    flight.onCompleted?.(event.message);
                }
                if (event.message.role === 'human_agent') {
                    this.showHumanMessage(event.message.id, event.message.content, false, event.message);
                }
                if (event.type === 'message.created' && event.message.role === 'system') {
                    this.showNotice(event.message);
                }
                return;
            case 'conversation.updated':
                if (event.patch?.handler === 'human' || event.patch?.handler === 'ai')
                    this.handler = event.patch.handler;
                if (event.patch?.handler === 'ai')
                    this.stopWriting();
                // Ended (by the team, or by this visitor elsewhere): the chat locks and
                // offers a new one. Abandoned stays open — writing again resumes it.
                if (event.patch?.status === 'completed') {
                    this.stopWriting();
                    this.push?.({ type: 'conversation.update', conversation: { status: 'closed' } });
                }
                return;
            case 'typing':
                // A person typing, or the AI drafting a reply for them (collaborate):
                // to the visitor it is the same thing — a reply on its way.
                if (event.role === 'human_agent')
                    this.setWriting('human', event.isTyping);
                else if (event.role === 'ai_agent')
                    this.setWriting('ai', event.isTyping);
                return;
            default:
                return;
        }
    }
    /** An unprompted turn: dots while it works, then its saved reply as one message. */
    onUnpromptedEvent(event, turnId, sending) {
        const emit = this.push;
        if (!emit || !this.chatId)
            return;
        switch (event.type) {
            case 'turn.status':
                if (!sending && (event.stage || event.progress)) {
                    emit({ type: 'status', stage: event.stage ?? '', ...(event.progress ? { text: event.progress } : {}) });
                }
                return;
            case 'turn.failed':
                this.unprompted.delete(turnId);
                if (!sending)
                    emit({ type: 'typing', isTyping: false });
                return;
            case 'message.completed': {
                if (event.message.role !== 'ai_agent')
                    return;
                this.unprompted.delete(turnId);
                if (!sending)
                    emit({ type: 'typing', isTyping: false });
                const message = event.message;
                const at = Date.parse(message.createdAt) || Date.now();
                emit({
                    type: 'message.start',
                    message: {
                        id: message.id,
                        conversationId: this.chatId,
                        role: 'assistant',
                        content: message.content,
                        status: 'streaming',
                        createdAt: at,
                        updatedAt: at,
                        author: AGENT_AUTHOR,
                    },
                });
                const suggestions = normaliseSuggestedActions(message.suggestedActions);
                if (suggestions?.length)
                    emit({ type: 'message.suggestions', messageId: message.id, suggestions });
                const blocks = normaliseContent(message.blocks ?? undefined);
                if (blocks?.length)
                    emit({ type: 'message.blocks', messageId: message.id, blocks });
                emit({ type: 'message.complete', messageId: message.id });
                return;
            }
            default:
                // Tokens are not streamed for it: the saved reply arrives whole.
                return;
        }
    }
    streamingShell(flight) {
        const now = Date.now();
        return {
            id: flight.replyId,
            conversationId: flight.chatId,
            role: 'assistant',
            content: '',
            status: 'streaming',
            createdAt: now,
            updatedAt: now,
            author: AGENT_AUTHOR,
        };
    }
    /**
     * A person is writing: the same dots the AI gets, worded as plain typing —
     * no "thinking" lines. Pings arrive every few seconds; silence ends it.
     */
    /**
     * The person typing ("human": pings every few seconds, silence ends it) or
     * the AI drafting for them ("ai": up from the draft's start until it is
     * sent, discarded or replaced — a draft waiting on review is still a reply on
     * its way). The same dots either way, worded as plain typing.
     */
    setWriting(who, isTyping) {
        const isAi = who === 'ai';
        clearTimeout(isAi ? this.aiTimer : this.typingTimer);
        this.writing[who] = isTyping;
        if (isTyping) {
            const timer = setTimeout(() => this.setWriting(who, false), isAi ? AI_DRAFTING_MAX_MS : HUMAN_TYPING_IDLE_MS);
            if (isAi)
                this.aiTimer = timer;
            else
                this.typingTimer = timer;
        }
        this.showWriting();
    }
    stopWriting() {
        clearTimeout(this.typingTimer);
        clearTimeout(this.aiTimer);
        this.writing = { ai: false, human: false };
        this.showWriting();
    }
    showWriting() {
        const emit = this.push;
        if (!emit)
            return;
        if (this.writing.ai || this.writing.human)
            emit({ type: 'status', stage: 'typing' });
        // Never cut the dots of a reply the visitor is waiting on from the AI.
        else if (!this.inFlight)
            emit({ type: 'typing', isTyping: false });
    }
    /** A person answering from the console. Human review — shown as it arrives. */
    showHumanMessage(id, text, isDelta, full) {
        const emit = this.push;
        if (!emit || !this.chatId)
            return;
        if (!isDelta)
            this.stopWriting();
        if (isDelta) {
            emit({ type: 'message.delta', messageId: id, delta: text });
            return;
        }
        const at = (full?.createdAt && Date.parse(full.createdAt)) || Date.now();
        emit({
            type: 'message.start',
            message: {
                id,
                conversationId: this.chatId,
                role: 'assistant',
                content: text,
                status: 'streaming',
                createdAt: at,
                updatedAt: at,
                author: { id: full?.author?.id ?? 'human', name: full?.author?.name || 'Support', kind: 'human' },
            },
        });
        emit({ type: 'message.complete', messageId: id });
    }
    /** A system line from the stream: "Priya joined the chat". */
    showNotice(message) {
        const emit = this.push;
        if (!emit || !this.chatId || !message.content)
            return;
        const at = Date.parse(message.createdAt) || Date.now();
        emit({
            type: 'message.start',
            message: {
                id: message.id,
                conversationId: this.chatId,
                role: 'system',
                content: message.content,
                status: 'streaming',
                createdAt: at,
                updatedAt: at,
            },
        });
        emit({ type: 'message.complete', messageId: message.id });
    }
    /** POST /api/v1/chats/{chatId}/messages — every message after the first. */
    async postMessage(message, context, signal) {
        if (!this.chatId || !this.sessionId) {
            throw chatError('unknown', 'Chat has not been started yet.', { retryable: false });
        }
        return this.request(`${this.prefix}/chats/${encodeURIComponent(this.chatId)}/messages`, {
            message,
            sessionId: this.sessionId,
            metadata: this.buildMetadata(context),
        }, signal);
    }
    /**
     * GET /api/v1/chats/{chatId}/results/{resultId}.csv — a data answer's whole
     * table. Authorised like every call (one retry on a refused token); the
     * server only serves this visitor's own chat.
     */
    async downloadResult(conversationId, resultId) {
        if (!/^[0-9a-f]{32}$/.test(resultId))
            throw new HttpError(400, 'Not a result id.');
        const path = `${this.prefix}/chats/${encodeURIComponent(conversationId)}/results/${resultId}.csv`;
        const fetchOnce = async (force) => this.doFetch(`${this.origin}${path}`, {
            method: 'GET',
            headers: { accept: 'text/csv', ...this.options.headers, ...(await this.authHeader(force)) },
        });
        let response = await fetchOnce(false);
        if (response.status === 401 && this.options.widgetKey)
            response = await fetchOnce(true);
        if (!response.ok) {
            throw new HttpError(response.status, response.status === 404 ? 'That table is no longer available.' : 'Download failed.');
        }
        return response.blob();
    }
    async get(path) {
        return this.send(path, { method: 'GET' });
    }
    async request(path, body, signal) {
        return this.send(path, { method: 'POST', body: JSON.stringify(body) }, signal);
    }
    /**
     * Every call to the API goes through here, which makes it the one place to
     * trace the wire. Both directions are logged at `debug` — visible while
     * developing, silent in a production build.
     *
     * Auth headers are never logged. Bodies carry whatever the visitor typed,
     * so `logBodies: false` keeps the timing and status lines without the
     * content.
     */
    async send(path, init, signal) {
        try {
            return await this.sendOnce(path, init, signal, await this.authHeader());
        }
        catch (err) {
            // The token expired early, or the tenant rotated its widget key: open a
            // new session and retry once. A second 401 is real and goes to the UI.
            if (!(err instanceof HttpError) || err.status !== 401 || !this.options.widgetKey)
                throw err;
            this.logger.debug('visitor token refused; opening a new session and retrying once', { path });
            return this.sendOnce(path, init, signal, await this.authHeader(true));
        }
    }
    /**
     * `Authorization` for the next call. Opens a widget session when there is
     * no token, it is within a minute of expiring, or `force` says it was refused.
     */
    async authHeader(force = false) {
        if (!this.options.widgetKey)
            return {};
        if (force || !this.token || Date.now() > this.tokenExpiresAt - 60_000) {
            this.opening ??= this.openSession().finally(() => {
                this.opening = undefined;
            });
            await this.opening;
        }
        return { Authorization: `Bearer ${this.token}` };
    }
    /**
     * POST /api/v1/widget/session — the public key buys a visitor token; with
     * `identity`, the host app's token signs the user in. A refused identity token
     * is fetched afresh once, then dropped: the session opens anonymously if the
     * tenant allows it, and fails with the API's message if it doesn't.
     */
    async openSession() {
        let identityToken = await this.identityToken();
        let attempt = await this.postSession(identityToken);
        if (identityToken && isIdentityRefusal(attempt)) {
            identityToken = await this.identityToken();
            attempt = identityToken ? await this.postSession(identityToken) : attempt;
            if (isIdentityRefusal(attempt)) {
                this.logger.warn('identity token refused twice; continuing without it');
                attempt = await this.postSession(null);
            }
        }
        const { status, path, payload } = attempt;
        if (!payload?.success || !payload.data) {
            this.logger.warn(`← ${status} POST ${path}`, { message: payload?.message, errors: payload?.errors ?? undefined });
            throw new HttpError(status, payload?.message ?? 'The chat is not available right now.', payload);
        }
        this.token = payload.data.access_token;
        this.tokenExpiresAt = Date.now() + payload.data.expires_in * 1000;
        this.signedIn = Boolean(payload.data.identity);
        // A signed-in user's id is theirs, on every device: never left in this
        // browser for the next (anonymous) visitor to send.
        if (!this.signedIn)
            this.storeVisitorId(payload.data.visitor_id);
        this.logger.debug(`← ${status} POST ${path}`, { expiresIn: payload.data.expires_in, signedIn: this.signedIn });
    }
    async postSession(identityToken) {
        const path = `${this.prefix}/widget/session`;
        const stored = identityToken ? null : this.storedVisitorId();
        const response = await this.doFetch(`${this.origin}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', accept: 'application/json' },
            body: JSON.stringify({
                widget_key: this.options.widgetKey,
                ...(stored ? { visitor_id: stored } : {}),
                ...(identityToken ? { identity_token: identityToken } : {}),
            }),
        });
        const text = await response.text();
        let payload = null;
        try {
            payload = text ? parseJsonLenient(text).value : null;
        }
        catch {
            payload = null;
        }
        return { status: response.status, path, payload: response.ok ? payload : payload && { ...payload, success: false } };
    }
    /**
     * POST /api/v1/widget/logout with the current token. Best effort and not
     * awaited: `keepalive` lets it finish when the page navigates away at
     * sign-out, and if it fails the token still expires on its own.
     */
    revokeOnServer() {
        if (!this.token || !this.options.widgetKey)
            return;
        const path = `${this.prefix}/widget/logout`;
        try {
            void this.doFetch(`${this.origin}${path}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${this.token}`, accept: 'application/json' },
                keepalive: true,
            }).catch((err) => this.logger.debug('sign-out not confirmed by the server', { error: String(err) }));
        }
        catch (err) {
            this.logger.debug('sign-out not sent', { error: String(err) });
        }
    }
    /** The host app's identity token, or null. Its failures never stop the chat. */
    async identityToken() {
        if (!this.options.identity)
            return null;
        try {
            const token = await this.options.identity.getToken();
            return typeof token === 'string' && token.trim() ? token.trim() : null;
        }
        catch (err) {
            this.logger.warn('identity.getToken() failed; continuing without it', { error: String(err) });
            return null;
        }
    }
    /**
     * The host app's user signed out. Tell Trinity, so every token this visitor
     * holds stops working now rather than at its expiry; then drop the session
     * and this browser's stored visitor id. The next session (and chat) starts
     * from nothing.
     */
    logout() {
        this.revokeOnServer();
        this.token = undefined;
        this.tokenExpiresAt = 0;
        this.signedIn = false;
        this.chatId = undefined;
        this.sessionId = undefined;
        this.closeStream();
        const key = this.options.visitorStorageKey ?? DEFAULT_VISITOR_KEY;
        if (key !== null && this.options.visitorId === undefined) {
            try {
                localStorage.removeItem(key);
            }
            catch {
                // private mode: nothing was stored
            }
        }
    }
    async sendOnce(path, init, signal, auth) {
        const controller = new AbortController();
        // Abort with a TimeoutError reason so a timeout surfaces as an error the
        // visitor sees, rather than being mistaken for them pressing stop.
        const timer = setTimeout(() => controller.abort(new DOMException('Request timed out.', 'TimeoutError')), this.options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
        signal?.addEventListener('abort', () => controller.abort(), { once: true });
        const method = (init.method ?? 'GET').toUpperCase();
        const label = `${method} ${path}`;
        const startedAt = Date.now();
        const withBodies = this.options.logBodies ?? true;
        this.logger.debug(`→ ${label}`, withBodies && init.body ? { body: decodeBody(init.body) } : undefined);
        try {
            const response = await this.doFetch(`${this.origin}${path}`, {
                ...init,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                    ...this.options.headers,
                    ...auth,
                },
            });
            // Not `response.json()`: the API emits unescaped newlines inside string
            // literals, which the strict parser rejects. See ./json.ts.
            const text = await response.text();
            let payload = null;
            if (text) {
                try {
                    const parsed = parseJsonLenient(text);
                    payload = parsed.value;
                    if (parsed.repaired && !AgentApiTransport.warnedAboutJson) {
                        AgentApiTransport.warnedAboutJson = true;
                        this.logger.warn('API returned invalid JSON (unescaped control characters in a string ' +
                            'literal). Recovered client-side; fix the serializer server-side.', { path });
                    }
                }
                catch {
                    payload = null;
                }
            }
            const ms = Date.now() - startedAt;
            if (!response.ok) {
                this.logger.warn(`← ${response.status} ${label} (${ms}ms)`, {
                    message: payload?.message,
                    errors: payload?.errors ?? undefined,
                });
                throw new HttpError(response.status, payload?.message ?? `Request failed: ${response.status}`, payload);
            }
            // A 2xx can still be a logical failure.
            if (!payload?.success || payload.data == null) {
                this.logger.warn(`← ${response.status} ${label} (${ms}ms) — success:false`, {
                    message: payload?.message,
                    errors: payload?.errors ?? undefined,
                });
                throw new HttpError(payload?.statusCode ?? response.status, payload?.message ?? 'The assistant could not answer.', payload);
            }
            this.logger.debug(`← ${response.status} ${label} (${ms}ms)`, withBodies ? { data: payload.data } : undefined);
            return payload.data;
        }
        catch (err) {
            // Network failure or timeout — no response ever arrived, so the
            // per-status logging above never ran.
            if (!(err instanceof HttpError)) {
                const ms = Date.now() - startedAt;
                const aborted = err instanceof DOMException && err.name === 'AbortError';
                this.logger[aborted ? 'debug' : 'warn'](`← ${aborted ? 'aborted' : 'failed'} ${label} (${ms}ms)`, aborted ? undefined : { error: err instanceof Error ? err.message : String(err) });
            }
            throw err;
        }
        finally {
            clearTimeout(timer);
        }
    }
    buildMetadata(context) {
        return { ...this.options.metadata, ...context };
    }
    toMessage(messageId, chatId, reply, data) {
        const now = Date.now();
        const sources = normaliseCitations(data.citations);
        // `pendingHandoffs` means the agent wants a human; surface it as an action.
        const actions = data.pendingHandoffs?.length
            ? [{ id: createId('act'), type: 'handoff', label: 'Talk to a person' }]
            : undefined;
        const blocks = normaliseContent(data.content);
        const suggestions = normaliseSuggestedActions(data.suggestedActions);
        return {
            id: messageId,
            conversationId: chatId,
            role: 'assistant',
            // The Markdown reply, and what the UI renders whenever it is non-empty.
            content: reply ?? '',
            blocks,
            status: 'complete',
            createdAt: now,
            updatedAt: now,
            author: AGENT_AUTHOR,
            sources,
            actions,
            suggestions,
            metadata: {
                records: data.records,
                isComplete: data.isComplete,
                usage: data.usage,
                pendingHandoffs: data.pendingHandoffs,
            },
        };
    }
    /**
     * The server stores the bootstrap message as a normal user turn, so a
     * resumed transcript would open with a "Hi" the visitor never typed —
     * while a fresh start hides it. Drop that first echo so both paths look
     * the same. Only an exact match in first position qualifies, so a genuine
     * "Hi" typed later survives.
     */
    dropBootstrapEcho(messages) {
        const bootstrap = this.options.bootstrapMessage?.trim();
        if (!bootstrap)
            return;
        const first = messages[0];
        if (first?.role === 'user' && first.content.trim() === bootstrap)
            messages.shift();
    }
    /** A stored transcript line carries no citations or usage — just the text. */
    fromTranscript(raw, chatId) {
        const at = (raw.createdAt && Date.parse(raw.createdAt)) || Date.now();
        const role = raw.role === 'user' || raw.role === 'system' ? raw.role : 'assistant';
        const blocks = role === 'assistant' ? normaliseContent(raw.blocks ?? undefined) : undefined;
        return {
            id: raw.id,
            conversationId: chatId,
            role,
            // The agent's Markdown when there is one, so a reloaded reply renders as it did live.
            content: (role === 'assistant' && raw.markdown) || raw.content || '',
            ...(blocks ? { blocks } : {}),
            status: 'complete',
            createdAt: at,
            updatedAt: at,
            author: role !== 'assistant'
                ? undefined
                : raw.senderRole === 'human_agent'
                    ? { id: raw.author?.id ?? 'human', name: raw.author?.name || 'Support', kind: 'human' }
                    : AGENT_AUTHOR,
        };
    }
    /**
     * Stable per-browser visitor id so the backend can recognise return visits.
     * With a widget key the SERVER issues it (and binds the token to it — a body
     * id is ignored), so it is only read here; without one a local id is made
     * up, as before.
     */
    /** `mode: 'data'` asks the server for a private chat (POST /chats). */
    modeField() {
        return this.options.mode === 'data' ? { mode: 'data' } : {};
    }
    resolveVisitorId() {
        if (this.options.visitorId !== undefined)
            return this.options.visitorId;
        const existing = this.storedVisitorId();
        if (existing || this.options.widgetKey)
            return existing;
        const fresh = createId('visitor');
        this.storeVisitorId(fresh);
        return fresh;
    }
    storedVisitorId() {
        if (this.options.visitorId !== undefined)
            return this.options.visitorId;
        const key = this.options.visitorStorageKey ?? DEFAULT_VISITOR_KEY;
        if (key === null)
            return null;
        try {
            return localStorage.getItem(key);
        }
        catch {
            return null; // private mode
        }
    }
    storeVisitorId(id) {
        const key = this.options.visitorStorageKey ?? DEFAULT_VISITOR_KEY;
        if (key === null || this.options.visitorId !== undefined)
            return;
        try {
            localStorage.setItem(key, id);
        }
        catch {
            // private mode: a fresh visitor id per page load
        }
    }
}
/**
 * Trims a trailing slash, and strips an accidentally-included API prefix.
 *
 * `baseUrl: '/api'` reads as correct but produces `/api/api/v1/chats`, so the
 * mistake is caught here rather than surfacing as a 404 at runtime.
 */
function normaliseOrigin(baseUrl, prefix, logger) {
    const trimmed = (baseUrl ?? '').replace(/\/$/, '');
    for (const suffix of [prefix, '/api']) {
        if (suffix && trimmed.endsWith(suffix)) {
            const corrected = trimmed.slice(0, -suffix.length);
            logger.warn('baseUrl already contains the API prefix; correcting it', {
                given: baseUrl,
                wouldRequest: `${trimmed}${prefix}/chats`,
                using: corrected || '(same origin)',
            });
            return corrected;
        }
    }
    return trimmed;
}
/**
 * Parses the `content` array, which backs the fallback render path for an
 * empty `reply`. The spec leaves block shapes open, so anything unrecognised
 * degrades to text when it carries a `text` field and is dropped otherwise —
 * a new block type on the backend can never blank out a reply.
 */
function normaliseContent(raw) {
    if (!Array.isArray(raw) || !raw.length)
        return undefined;
    const blocks = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const id = typeof item.id === 'string' ? { id: item.id } : {};
        if (item.type === 'table') {
            const columns = toStringArray(item.columns);
            const rows = Array.isArray(item.rows) ? item.rows.map(toStringArray) : [];
            // A table with no columns and no rows has nothing to render.
            if (columns.length || rows.length) {
                const types = toStringArray(item.columnTypes).map(toFormat);
                blocks.push({
                    type: 'table',
                    ...id,
                    columns,
                    rows,
                    ...(item.caption ? { caption: String(item.caption) } : {}),
                    ...(types.length === columns.length && types.length ? { columnTypes: types } : {}),
                    ...(typeof item.totalRows === 'number' ? { totalRows: item.totalRows } : {}),
                    ...(item.truncated === true ? { truncated: true } : {}),
                    ...(typeof item.resultId === 'string' && /^[0-9a-f]{32}$/.test(item.resultId)
                        ? { resultId: item.resultId }
                        : {}),
                });
            }
            continue;
        }
        if (item.type === 'kpi') {
            const items = (Array.isArray(item.items) ? item.items : [])
                .filter((k) => !!k && typeof k === 'object')
                .map((k) => ({
                label: String(k.label ?? ''),
                value: typeof k.value === 'number' || typeof k.value === 'string' ? k.value : null,
                display: String(k.display ?? k.value ?? ''),
                format: toFormat(k.format),
            }))
                .filter((k) => k.label || k.display);
            if (items.length)
                blocks.push({ type: 'kpi', ...id, items });
            continue;
        }
        if (item.type === 'chart') {
            const kind = item.kind === 'line' || item.kind === 'pie' || item.kind === 'bar' ? item.kind : undefined;
            const x = item.x && typeof item.x === 'object' ? item.x : undefined;
            const series = (Array.isArray(item.series) ? item.series : [])
                .filter((s) => !!s && typeof s === 'object')
                .map((s) => ({ key: String(s.key ?? ''), label: String(s.label ?? s.key ?? ''), format: toFormat(s.format) }));
            const rows = (Array.isArray(item.rows) ? item.rows : [])
                .filter((r) => Array.isArray(r) && r.length === series.length + 1)
                .map((r) => r.map((v) => (typeof v === 'number' || typeof v === 'string' ? v : null)));
            if (kind && x && series.length && rows.length >= 2) {
                blocks.push({
                    type: 'chart',
                    ...id,
                    kind,
                    title: String(item.title ?? ''),
                    x: { key: String(x.key ?? ''), label: String(x.label ?? x.key ?? '') },
                    series,
                    rows,
                    ...(typeof item.currency === 'string' ? { currency: item.currency } : {}),
                });
            }
            continue;
        }
        if (item.type === 'details') {
            const items = toStringArray(item.items).filter((t) => t.trim());
            if (items.length)
                blocks.push({ type: 'details', ...id, title: String(item.title ?? 'Details'), items });
            continue;
        }
        if (item.type === 'action') {
            if (typeof item.action === 'string' && item.action && typeof item.label === 'string' && item.label.trim()) {
                blocks.push({ type: 'action', ...id, action: item.action, label: item.label });
            }
            continue;
        }
        const text = typeof item.text === 'string' ? item.text : undefined;
        if (text === undefined || !text.trim())
            continue;
        blocks.push({
            type: 'text',
            text,
            ...(typeof item.role === 'string' ? { role: item.role } : {}),
        });
    }
    return blocks.length ? blocks : undefined;
}
const FORMATS = ['text', 'money', 'count', 'number', 'pct', 'label'];
function toFormat(value) {
    return FORMATS.includes(value) ? value : 'text';
}
/** `suggestedActions` is `string[]`; objects are tolerated for future use. */
/**
 * Whether a failed POST may still be followed by its reply: anything that
 * ended the request rather than the turn. Only `reply_failed` (every attempt
 * spent) and client errors are final.
 */
function isStillRunning(err) {
    if (err instanceof DOMException)
        return err.name === 'TimeoutError';
    if (err instanceof TypeError)
        return true; // the connection dropped
    if (!(err instanceof HttpError))
        return false;
    if (err.status < 500 && err.status !== 408)
        return false;
    const body = err.body;
    return !(body?.errors ?? []).some((e) => e?.code === 'reply_failed');
}
function normaliseSuggestedActions(raw) {
    if (!Array.isArray(raw) || !raw.length)
        return undefined;
    const suggestions = raw
        .map((item, index) => {
        if (typeof item === 'string') {
            return item.trim() ? { id: `sa${index}`, label: item.trim() } : null;
        }
        if (item && typeof item === 'object') {
            const o = item;
            const label = o.label ?? o.title ?? o.text;
            if (typeof label !== 'string' || !label.trim())
                return null;
            return {
                id: String(o.id ?? `sa${index}`),
                label: label.trim(),
                ...(typeof o.value === 'string' ? { value: o.value } : {}),
            };
        }
        return null;
    })
        .filter((s) => s !== null);
    return suggestions.length ? suggestions : undefined;
}
function toStringArray(raw) {
    return Array.isArray(raw) ? raw.map((cell) => (cell == null ? '' : String(cell))) : [];
}
/** Request bodies are JSON strings by the time they reach `send`. */
function decodeBody(body) {
    if (typeof body !== 'string')
        return '(non-string body)';
    try {
        return JSON.parse(body);
    }
    catch {
        return body;
    }
}
/** Citations are unspecified in the samples, so accept the plausible shapes. */
function normaliseCitations(citations) {
    if (!Array.isArray(citations) || !citations.length)
        return undefined;
    return citations.map((raw, index) => {
        if (typeof raw === 'string') {
            return { id: `c${index}`, title: raw };
        }
        const c = (raw ?? {});
        return {
            id: String(c.id ?? `c${index}`),
            title: String(c.title ?? c.name ?? c.source ?? `Source ${index + 1}`),
            url: c.url ?? c.link,
            snippet: c.snippet ?? c.text ?? c.content,
        };
    });
}
/** The API refused the identity token (as opposed to the widget key, origin or rate). */
function isIdentityRefusal(attempt) {
    if (attempt.status !== 401 || attempt.payload?.success)
        return false;
    const errors = attempt.payload?.errors;
    return Array.isArray(errors) && errors.some((e) => {
        const code = e?.code;
        // REPLAYED: that token was already used once; a fresh one from the host
        // app's endpoint opens the session.
        return code === 'IDENTITY_INVALID' || code === 'IDENTITY_EXPIRED' || code === 'IDENTITY_REPLAYED';
    });
}
